import React, {
  type ComponentType,
  PureComponent,
  type ReactElement,
  type ReactNode,
} from 'react';

import classNames from 'classnames';
import {
  debounce,
  get,
  identity,
  isArray,
  isNil,
  isObject,
  isString,
  noop,
  uniq,
  uniqBy,
} from 'lodash-es';

import getRandomHex from '@iconik-io/core/src/get_random_hex';
import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';
import { Icon } from '@iconik-io/uad';

import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import { INPUT_DEBOUNCE_TIMEOUT } from '../../constants/debounce';
import { targetElement } from '../../utils/event_target';
import { ui, updateObjectAtIndex } from '../../utils/helpers';
import { uniqueKey } from '../../utils/unique_key';
import Button from '../button/button';
import CheckboxBasic from '../checkbox_basic/checkbox_basic';
import { isHoldingOnlyCtrlKey, removeSuffix } from '../forms/utils/utils';
import LoadingBar from '../loading_bar/loading_bar';
import Popover from '../popover/popover';
import type { VDirection } from '../popover/utils';
import Option from './option';
import type {
  OptionComponentProps,
  SelectOption,
  ValueComponentProps,
} from './types';
import { explodeLoadOptionsResult, type LoadOptionsResult } from './utils';
import Value from './value';

import mixins from '../../styles/core/mixins.module.css';
import styles from './select_field.module.css';

function setTimeoutOrImmediately(fn: () => void, timeout: number | undefined) {
  if (timeout) {
    return window.setTimeout(fn, timeout);
  }
  fn();
  return undefined;
}

function getScrollableParents(element: HTMLElement | null): HTMLElement[] {
  if (element === null) {
    return [];
  }

  const result = getScrollableParents(element.parentElement);

  if (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  ) {
    result.push(element);
  }

  return result;
}

const FAKE_OPTION_HEIGHT = 34;

export const NEW_OPTION_VALUE = '<new_option_value>';
export const NEW_OPTION_VALUE_REGEX = /^<new_option_value_[0-9a-f]*>$/;

export const generateNewOptionValue = (isMulti = false): string =>
  `<new_option_value_${isMulti ? getRandomHex() : 0}>`;

/**
 * `V` value type.
 * `O` source option type (remote or from `options` prop).
 * `IO` internal option type. Needed as sometimes we store additional data in selected options. Must be compatible
 *   with SelectOption type.
 */
export type SelectFieldProps<
  V = string,
  O = SelectOption,
  IO extends SelectOption = SelectOption,
> = {
  className?: string;
  name?: string;
  label?: string;
  value?: string | V[];
  options: O[];
  isCreatorMode: boolean;
  isMulti: boolean;
  // Warning for values that was deleted in metadata configuration.
  warning?: { warningLabel: string; flaggedValue: string };
  // If true and isMulti mode is on, options list will be filtered and already selected
  // options will be hidden.
  hideSelected: boolean;
  onNewOptionNameSelected: (label: string) => void;

  // Converts a source option (from remote source or which is passed through the options prop)
  // to an internal option representation.
  optionSourceToSelect: (value: O) => IO;

  valueSourceToSelect: (value: V) => IO;

  valueSelectToSource: (value: IO) => V;

  // Additional processing of an option before rendering as an option value (the item in the input).
  // Available only for the multi mode.
  processOptionValue: (option: IO) => IO;

  // Additional processing for the new option when it is created via the creator mode.
  processNewOptionValue: (option: IO) => IO;

  // If true, the value will be updated with the first option from the options list.
  // Works with passed array of options or with async options in single-select mode.
  selectFirstAvailableOption: boolean;

  // If true, replaces the current selection with the first available option when the
  // selected value is no longer present in the options list (e.g. it was removed remotely).
  // Requires single-select mode and `selectFirstAvailableOption` to be enabled.
  selectFirstWhenValueMissing: boolean;
  preloadOptions: boolean;
  placeholder: string;
  creatorText: string;
  creatorAction: string;
  readOnly?: boolean;
  canClear?: boolean;
  clearValue: string | null;
  onRemoteOptionsSet?: () => void;
  onBlur?: (
    event:
      | React.FocusEvent<HTMLInputElement>
      | React.FocusEvent<HTMLSelectElement>,
  ) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  loadOptions?: (query: string) => Promise<LoadOptionsResult<O>>;

  // Used during the initialization when selected option is not in the list
  // of preloaded options, basically to prevent "Not selected"
  loadSelectedOptions?: (ids: string[]) => Promise<O[]>;

  newOptionValue: (option: IO) => string;
  valuePrimaryKey?: string;
  valueComponent?: ComponentType<ValueComponentProps<IO>>;
  optionComponent?: ComponentType<OptionComponentProps>;
  isFormDisabled: boolean;
  id?: string;
  onChange?: (value: string | V[] | undefined | null) => void;
  filterOption: (option: IO, filterValue: string) => boolean;
  ignoreIds?: string[];

  // If true in multi mode the select field will treat values as a plain array of string values,
  // for showing selected items it will try to find the corresponding options from options list.
  // If false all values will be treated as rich value objects { value: string; label: string }.
  plainMultiValues: boolean;

  optionsListDelayMs?: number;
  hideOptionsList?: boolean;

  // These elements will be shown in the options list matched to each specified value in the end of row.
  // They also will be shown before action elements once the selected value is matched to a marked value.
  markedValueElements?: {
    [value: string]: ReactElement;
  };

  aria?: AriaProps;

  // Enables checkbox selection for options in the options list. Must be used with the `isMulti` prop true.
  withCheckboxSelection?: boolean;

  // Opt-in to native value copy/paste (Ctrl+A to mark, Ctrl+C to copy, paste to
  // add) handled internally by the field. Only meaningful for multi fields.
  enableValueCopyPaste?: boolean;

  // Notifications so the app layer can surface feedback for copy, keeping the
  // field free of any app/redux dependency.
  onValuesCopied?: () => void;
  onCopyError?: (message: string) => void;

  // Keeps available options empty if the filter value is empty.
  emptyOptionsOnEmptyFilter?: boolean;
  noOptionsPlaceholder?: string;

  // This flag is used to allow filter value that already exists in the select options list
  // to added as new option.
  // This is especially useful for adding the same person name for detected faces.
  allowDuplicateSelectOptions?: boolean;
};

type State<O> = {
  show: boolean;
  focused: boolean;
  filterValue: string;
  newOptionName: string;
  hoverIndex: number;
  isLoading: boolean;

  // If true then asynchronously loaded options are not fully loaded, so only the first page
  // of results will be shown. Used to show a marker "More options available".
  incomplete: boolean;
  remoteOptions: O[];
  selectedOptions: O[];
  remoteOptionsInitialized: boolean;
  popoverDirection: VDirection;

  // Value copy/paste (opt-in via `enableValueCopyPaste`). `copyPasteActive`
  // tracks whether the field is the one interacted with (tracked at the root
  // level so clicking value chips does not end copy mode), `holdingModifier`
  // whether Ctrl/Cmd is held, and `copySelection` the values marked to copy.
  copyPasteActive: boolean;
  holdingModifier: boolean;
  copySelection: string[];
};

const filterOptionDefault = (
  option: SelectOption,
  filterValue: string,
): boolean => option.label.toLowerCase().includes(filterValue.toLowerCase());

const newOptionValueDefault = (option: SelectOption): string => option.label;

/**
 * With `plainMultiValues` the multi value array holds bare option ids instead
 * of option objects, so every reader of that array meets both shapes.
 */
type SelectValueItem<IO> = IO | string;

export const valueItemId = <IO extends SelectOption>(
  item: SelectValueItem<IO>,
): string => (typeof item === 'string' ? item : item.value);

/**
 * The other half of `plainMultiValues`: for those callers `V` is the option id,
 * but the props cannot say so. `V` is fixed when the field is instantiated and
 * `plainMultiValues` is a runtime flag on that same instance.
 * https://linear.app/iconik/issue/FE-7732
 */
// biome-ignore lint/plugin: see above.
const plainValue = <V,>(id: string): V => id as V;

/**
 * A brand new option starts as a bare label/value pair. `processNewOptionValue`
 * is the caller's hook to add whatever else its own IO carries, so only the
 * caller can complete the shape. See select_persons, which adds
 * personVersionFaceImageUrl there.
 */
const newOption = <IO extends SelectOption>(value: string, label: string): IO =>
  // biome-ignore lint/plugin: see above.
  ({ value, label }) as IO;

class SelectField<
  V = string,
  O = SelectOption,
  IO extends SelectOption = SelectOption,
> extends PureComponent<SelectFieldProps<V, O, IO>, State<O>> {
  static defaultProps = {
    options: [],
    isFormDisabled: false,
    isCreatorMode: false,
    isMulti: false,
    hideSelected: true,
    onNewOptionNameSelected: noop,
    optionSourceToSelect: identity,
    valueSourceToSelect: identity,
    valueSelectToSource: identity,
    processOptionValue: identity,
    processNewOptionValue: identity,
    selectFirstAvailableOption: false,
    selectFirstWhenValueMissing: false,
    placeholder: 'Not selected',
    creatorText: 'Type to create a new',
    creatorAction: 'Create',
    preloadOptions: false,
    filterOption: filterOptionDefault,
    plainMultiValues: false,
    newOptionValue: newOptionValueDefault,
    clearValue: '',
    allowDuplicateSelectOptions: false,
  };

  remoteOptionsCache = new Map<string, LoadOptionsResult<O>>();

  id?: string;

  promises: Promise<void>[] = [];

  rootRef = React.createRef<HTMLDivElement>();

  optionsListRef = React.createRef<HTMLDivElement>();

  filterInputRef = React.createRef<HTMLInputElement>();

  popoverRef = React.createRef<HTMLDivElement>();

  optionsListHeaderRef = React.createRef<HTMLDivElement>();

  state: State<O> = {
    show: false,
    focused: false,
    filterValue: '',
    newOptionName: '',
    hoverIndex: 0,
    isLoading: false,
    incomplete: false,
    remoteOptions: [],
    selectedOptions: [],
    remoteOptionsInitialized: false,
    popoverDirection: 'bottom',
    copyPasteActive: false,
    holdingModifier: false,
    copySelection: [],
  };

  componentUnmounted = false;

  componentMounted = false;

  timeoutId: number | undefined = undefined;

  componentDidMount(): void {
    this.componentMounted = true;
    const { preloadOptions, options, isMulti, selectFirstAvailableOption } =
      this.props;

    document.addEventListener('keydown', this.handleDocumentKeyDown);

    if (this.props.enableValueCopyPaste) {
      document.addEventListener(
        'mousedown',
        this.handleCopyPasteFocusChange,
        true,
      );
      document.addEventListener(
        'focusin',
        this.handleCopyPasteFocusChange,
        true,
      );
      document.addEventListener('keyup', this.handleCopyPasteKeyUp);
      document.addEventListener('copy', this.handleCopy);
      document.addEventListener('paste', this.handlePaste);
    }

    let preloadPromise: Promise<void> | undefined;
    if (preloadOptions) {
      preloadPromise = this.loadRemoteOptions().then(() =>
        this.loadCurrentOptions(),
      );
    } else {
      preloadPromise = this.loadCurrentOptions();
    }

    preloadPromise.finally(() => {
      if (this.componentMounted) {
        this.setState({
          remoteOptionsInitialized: true,
        });
      }
    });

    if (
      !preloadOptions &&
      !isMulti &&
      selectFirstAvailableOption &&
      this.isValueEmpty()
    ) {
      const optionsValue = get(options, [0, 'value']);
      if (optionsValue) {
        this.updateValue(optionsValue);
      }
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    if (this.props.enableValueCopyPaste) {
      document.removeEventListener(
        'mousedown',
        this.handleCopyPasteFocusChange,
        true,
      );
      document.removeEventListener(
        'focusin',
        this.handleCopyPasteFocusChange,
        true,
      );
      document.removeEventListener('keyup', this.handleCopyPasteKeyUp);
      document.removeEventListener('copy', this.handleCopy);
      document.removeEventListener('paste', this.handlePaste);
    }
    this.componentMounted = false;
    this.componentUnmounted = true;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  componentDidUpdate(prev: SelectFieldProps<V, O, IO>): void {
    if (
      prev.isMulti !== this.props.isMulti ||
      prev.withCheckboxSelection !== this.props.withCheckboxSelection ||
      prev.allowDuplicateSelectOptions !==
        this.props.allowDuplicateSelectOptions
    ) {
      this.warnInvalidDupFlag(this.props);
    }
  }

  warnInvalidDupFlag(props: SelectFieldProps<V, O, IO>): void {
    if (
      props.allowDuplicateSelectOptions &&
      (props.isMulti || props.withCheckboxSelection)
    ) {
      // biome-ignore lint/suspicious/noConsole: warning hint
      console.warn(
        '[SelectField] `allowDuplicateSelectOptions` was set to `true` but `isMulti` or `withCheckboxSelection` is also `true`.  Duplicates will be disabled.',
      );
    }
  }

  effectiveAllowDuplicates(): boolean {
    return (
      Boolean(this.props.allowDuplicateSelectOptions) &&
      !this.props.isMulti &&
      !this.props.withCheckboxSelection
    );
  }

  getAllSourceOptions = (): O[] => {
    const { options, optionSourceToSelect, loadOptions, loadSelectedOptions } =
      this.props;
    const { remoteOptions, selectedOptions } = this.state;

    const result = options.concat(selectedOptions, remoteOptions);
    if (!loadOptions && !loadSelectedOptions) {
      // For select fields with local options only we allow them to have duplicated values as in that
      // case handling of duplication issues are completely up to the local options source.
      return result;
    }
    return uniqBy(result, (option) => optionSourceToSelect(option).value);
  };

  getAllOptions = (): IO[] => {
    const allSourceOptions = this.getAllSourceOptions();
    let options = allSourceOptions.map(this.props.optionSourceToSelect);
    this.remoteOptionsCache.forEach((result) => {
      const optionsToAdd = Array.isArray(result) ? result : result.options;
      options = options.concat(
        optionsToAdd.map(this.props.optionSourceToSelect),
      );
    });

    return options;
  };

  tryPrependNewOption(newOptionName: string, options: IO[]) {
    const createOption = this.props.processNewOptionValue(
      newOption<IO>(generateNewOptionValue(this.props.isMulti), newOptionName),
    );

    if (createOption.label !== '') {
      return [createOption, ...options];
    }

    return options;
  }

  getRenderOptions(): IO[] {
    const {
      isCreatorMode,
      hideSelected,
      value,
      filterOption,
      plainMultiValues,
      valueSourceToSelect,
      optionSourceToSelect,
    } = this.props;
    const { filterValue, newOptionName } = this.state;
    const optionsListHaveCheckboxes =
      this.props.isMulti && this.props.withCheckboxSelection;

    let allSourceOptions = this.getAllSourceOptions();

    if (this.isMultiValue(value) && hideSelected) {
      const values = (value || []).map((item) =>
        plainMultiValues ? item : valueSourceToSelect(item).value,
      );
      const selectedSet = new Set(values);
      if (!optionsListHaveCheckboxes) {
        allSourceOptions = allSourceOptions.filter(
          (option) => !selectedSet.has(optionSourceToSelect(option).value),
        );
      }
    }

    const options = allSourceOptions.map(optionSourceToSelect);

    let result = !filterValue
      ? options
      : options.filter((option) => filterOption(option, filterValue));

    const isOptionExists = result.some(
      (option) => option.label.toLowerCase() === filterValue.toLowerCase(),
    );
    const canBeIncludedInSelectOptions =
      !isOptionExists || this.effectiveAllowDuplicates();

    if (
      isCreatorMode &&
      (filterValue || newOptionName) &&
      canBeIncludedInSelectOptions
    ) {
      result = this.tryPrependNewOption(newOptionName, result);
    }

    return result;
  }

  isMultiSelectOption = (
    value: SelectValueItem<IO>[] | string | null | undefined,
  ): value is SelectValueItem<IO>[] => this.props.isMulti;

  isMultiValue = (value: string | V[] | undefined): value is V[] =>
    this.props.isMulti;

  isSingleValue = (value: string | V[]): value is string => !this.props.isMulti;

  updateValue(option: SelectValueItem<IO>[] | string | null | undefined): void {
    const {
      onNewOptionNameSelected,
      onChange,
      isMulti,
      newOptionValue,
      valuePrimaryKey,
      valueSelectToSource,
    } = this.props;

    if (this.isMultiSelectOption(option)) {
      let processedValue: V[];
      processedValue = option
        .map((item) => {
          if (
            typeof item === 'string' ||
            !NEW_OPTION_VALUE_REGEX.test(item.value)
          ) {
            return item;
          }

          return {
            ...item,
            value: newOptionValue ? newOptionValue(item) : uniqueKey(),
          };
        })
        .map((item) =>
          typeof item === 'string'
            ? plainValue<V>(item)
            : valueSelectToSource(item),
        );

      if (processedValue.every((valueItem) => isObject(valueItem))) {
        processedValue = uniqBy(processedValue, valuePrimaryKey ?? 'value');
      } else {
        processedValue = uniq(processedValue);
      }
      onChange?.(processedValue);
    } else if (isString(option) || isNil(option)) {
      onChange?.(option);
    }

    if (!isMulti) {
      if (!(isString(option) && NEW_OPTION_VALUE_REGEX.test(option))) {
        this.setState({ newOptionName: '' });
        onNewOptionNameSelected('');
      } else {
        onNewOptionNameSelected(this.state.newOptionName);
      }
    } else {
      // Select field width based on the current value will be updated only on the next tick,
      // options list width depends on the select field width, so we can't do this synchronously
      // and postpone it to the next tick instead.
      setTimeout(() => {
        this.refreshOptionsListWidth();
      }, 0);
    }
  }

  scrollToOption(optionIndex: number): void {
    const { current } = this.optionsListRef;
    if (!current) {
      return;
    }
    const { scrollTop } = current;
    const { height } = current.getBoundingClientRect();
    const itemPosition = FAKE_OPTION_HEIGHT * optionIndex;
    if (itemPosition < scrollTop) {
      current.scrollTop = FAKE_OPTION_HEIGHT * optionIndex;
    }

    if (itemPosition > scrollTop + height - FAKE_OPTION_HEIGHT) {
      current.scrollTop =
        FAKE_OPTION_HEIGHT * optionIndex - height + FAKE_OPTION_HEIGHT + 15;
    }
  }

  isValueCopyMode(): boolean {
    return Boolean(
      this.props.enableValueCopyPaste &&
        this.state.copyPasteActive &&
        this.state.holdingModifier,
    );
  }

  getCurrentValueStrings(): string[] {
    const valueOption = this.getSelectValue(this.props.value);
    if (!Array.isArray(valueOption)) {
      return [];
    }
    return valueOption.map(valueItemId);
  }

  // Track whether this field is the one being interacted with, at the root
  // level, so clicking value chips (which blurs the input) does not end copy
  // mode. Handling focusin covers keyboard tabbing that a mousedown alone
  // would miss.
  handleCopyPasteFocusChange = (event: Event): void => {
    const isInside =
      this.rootRef.current?.contains(targetElement(event.target) ?? null) ??
      false;
    if (isInside) {
      if (!this.state.copyPasteActive) {
        this.setState({ copyPasteActive: true });
      }
    } else if (this.state.copyPasteActive) {
      this.setState({
        copyPasteActive: false,
        holdingModifier: false,
        copySelection: [],
      });
    }
  };

  handleCopyPasteKeyUp = (event: KeyboardEvent): void => {
    if (
      !isHoldingOnlyCtrlKey(event) &&
      (this.state.holdingModifier || this.state.copySelection.length)
    ) {
      this.setState({ holdingModifier: false, copySelection: [] });
    }
  };

  toggleCopySelection = (value: string): void => {
    this.setState((prev) => ({
      copySelection: prev.copySelection.includes(value)
        ? prev.copySelection.filter((item) => item !== value)
        : [...prev.copySelection, value],
    }));
  };

  handleCopy = (event: ClipboardEvent): void => {
    if (
      !this.state.copyPasteActive ||
      !this.state.holdingModifier ||
      !this.state.copySelection.length
    ) {
      return;
    }
    try {
      event.clipboardData?.setData(
        'text/plain',
        this.state.copySelection.join(','),
      );
      this.props.onValuesCopied?.();
    } catch (error) {
      this.props.onCopyError?.(
        `Please check clipboard access in your browser settings: ${error}`,
      );
    }
    event.preventDefault();
  };

  handlePaste = (event: ClipboardEvent): void => {
    if (!this.state.copyPasteActive) {
      return;
    }
    let pasted: string;
    try {
      pasted = event.clipboardData?.getData('text/plain') ?? '';
    } catch (error) {
      this.props.onCopyError?.(
        `Please check clipboard access in your browser settings: ${error}`,
      );
      return;
    }
    if (!pasted) {
      return;
    }
    const pastedValues = pasted
      .split(',')
      .map((item) => item.replace(/(\r\n|\n|\r)/gm, ' ').trim())
      .filter(Boolean);
    if (!pastedValues.length) {
      return;
    }
    event.preventDefault();

    const currentValueOption = this.getSelectValue(this.props.value);
    if (!Array.isArray(currentValueOption)) {
      return;
    }

    let newItems: IO[];
    if (this.props.isCreatorMode) {
      // Same path as typing a new option, so it gets the same hook.
      newItems = pastedValues.map((value) =>
        this.props.processNewOptionValue(newOption<IO>(value, value)),
      );
    } else {
      const optionByValue = new Map<string, IO>();
      this.getAllOptions().forEach((option) => {
        if (typeof option.value === 'string') {
          optionByValue.set(option.value.toLowerCase(), option);
        }
      });
      newItems = pastedValues
        .map((value) => optionByValue.get(value.toLowerCase()))
        .filter((option): option is IO => Boolean(option));
    }
    if (!newItems.length) {
      return;
    }
    this.updateValue([...currentValueOption, ...newItems]);
  };

  handleDocumentKeyDown = (event: KeyboardEvent): void => {
    const { plainMultiValues } = this.props;

    if (this.props.enableValueCopyPaste && this.state.copyPasteActive) {
      const holdingModifier = isHoldingOnlyCtrlKey(event);
      if (holdingModifier !== this.state.holdingModifier) {
        this.setState({ holdingModifier });
      }
      if (holdingModifier && event.code === 'KeyA') {
        const values = this.getCurrentValueStrings();
        if (values.length) {
          event.preventDefault();
          this.setState({ copySelection: values });
        }
      }
    }

    // Escape
    if (this.state.show && event.code === 'Escape') {
      this.close();
    }

    // Enter
    // Tab (only when there is filterValue, so we don't block tab navigation)
    const isEnterOrTab =
      event.code === 'Enter' ||
      (event.code === 'Tab' && this.state.filterValue);
    const isModifierKey =
      event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
    if (isEnterOrTab && !isModifierKey && this.state.show) {
      const renderOptions = this.getRenderOptions();
      const selectedOption = renderOptions[this.state.hoverIndex];
      if (selectedOption) {
        const { value: valueProp } = this.props;
        const selectValue = this.getSelectValue(valueProp);
        if (this.isMultiSelectOption(selectValue)) {
          const optionToAdd = plainMultiValues
            ? selectedOption.value
            : selectedOption;
          this.updateValue(selectValue.concat(optionToAdd));
        } else {
          this.updateValue(selectedOption.value);
        }
        event.preventDefault();
      }
      this.close();
    }

    let nextHoverIndex: number | undefined;

    // Up
    if (event.code === 'ArrowUp' && this.state.hoverIndex && this.state.show) {
      nextHoverIndex = this.state.hoverIndex - 1;
    }

    // Down
    if (event.code === 'ArrowDown' && this.state.show) {
      const renderOptions = this.getRenderOptions();

      if (this.state.hoverIndex < renderOptions.length - 1) {
        nextHoverIndex = this.state.hoverIndex + 1;
      }
    }

    // Backspace
    const { value = [] } = this.props;
    if (
      event.code === 'Backspace' &&
      this.state.focused &&
      this.state.filterValue === '' &&
      this.isMultiValue(value) &&
      value.length > 0
    ) {
      event.preventDefault();
      if (!this.isValueDisabled(value[value.length - 1])) {
        this.updateValue(
          value.slice(0, -1).map(this.props.valueSourceToSelect),
        );
      }
    }

    if (typeof nextHoverIndex !== 'undefined' && nextHoverIndex >= 0) {
      this.setState({
        hoverIndex: nextHoverIndex,
      });

      this.scrollToOption(nextHoverIndex);
    }
  };

  isValueDisabled = (value: V): boolean => {
    const id = valueItemId(this.props.valueSourceToSelect(value));
    const option = this.getAllOptions().find(
      (candidate) => candidate.value === id,
    );

    return Boolean(option && this.props.processOptionValue(option).disabled);
  };

  refreshOptionsListPosition = (rect?: DOMRect): void => {
    const { innerHeight } = window;
    const { current: listElement } = this.optionsListRef;
    const { current: rootElement } = this.rootRef;
    const { current: popoverElement } = this.popoverRef;
    if (!listElement || !rootElement || !popoverElement) {
      return;
    }
    const {
      top: rootTop,
      bottom: rootBottom,
      left: rootLeft,
    } = rootElement.getBoundingClientRect();
    const height = rect?.height ?? listElement.getBoundingClientRect().height;

    popoverElement.style.left = `${rootLeft}px`;
    if (rootBottom + height > innerHeight) {
      popoverElement.style.top = `${rootTop}px`;
      this.setState({
        popoverDirection: 'top',
      });
    } else {
      popoverElement.style.top = `${rootBottom}px`;
      this.setState({
        popoverDirection: 'bottom',
      });
    }
    this.refreshOptionsListWidth();
  };

  refreshOptionsListWidth = (): void => {
    const { current: listElement } = this.optionsListRef;
    const { current: rootElement } = this.rootRef;
    if (!rootElement) {
      return;
    }
    const { width } = rootElement.getBoundingClientRect();

    if (listElement) {
      listElement.style.width = `${width}px`;
    }
  };

  handleSideEffectClose = (): void => {
    this.close();
  };

  open(): void {
    if (this.state.show) {
      return;
    }

    let hoverIndex = 0;
    const { value, options, optionSourceToSelect } = this.props;
    if (value && this.isSingleValue(value)) {
      hoverIndex = options.findIndex(
        (option) => optionSourceToSelect(option).value === value,
      );
    }

    this.loadRemoteOptions(this.state.filterValue);

    const scrollables = getScrollableParents(this.rootRef.current);
    if (scrollables) {
      for (const scrollable of scrollables) {
        scrollable.addEventListener('scroll', this.handleSideEffectClose);
      }
    }
    window.addEventListener('resize', this.handleSideEffectClose);

    this.timeoutId = setTimeoutOrImmediately(() => {
      this.setState(
        {
          show: true,
          hoverIndex,
        },
        () => {
          this.scrollToOption(hoverIndex);
        },
      );
    }, this.props.optionsListDelayMs ?? 0);
  }

  close(): void {
    this.setState({
      show: false,
      filterValue: '',
      hoverIndex: 0,
      isLoading: false,
    });

    const { isMulti } = this.props;

    if (isMulti) {
      this.setState({
        newOptionName: '',
      });
    }

    const scrollables = getScrollableParents(this.rootRef.current);
    if (scrollables) {
      for (const scrollable of scrollables) {
        scrollable.removeEventListener('scroll', this.handleSideEffectClose);
      }
    }
    window.removeEventListener('resize', this.handleSideEffectClose);
  }

  isValueEmpty = (): boolean => {
    const { value } = this.props;
    if (this.isMultiValue(value)) {
      return value.length === 0;
    }
    return !value;
  };

  tryToSelectFirstOption = (options: O[]): void => {
    const {
      isMulti,
      selectFirstAvailableOption,
      selectFirstWhenValueMissing,
      value,
      optionSourceToSelect,
    } = this.props;
    if (isMulti || !selectFirstAvailableOption) {
      return;
    }
    // Skip auto-selection while the user is filtering.
    if (this.state.filterValue) {
      return;
    }
    const shouldReplaceStaleValue =
      selectFirstWhenValueMissing &&
      !this.isValueEmpty() &&
      !options.some((option) => optionSourceToSelect(option).value === value);
    const shouldSelectFirst = this.isValueEmpty() || shouldReplaceStaleValue;
    if (!shouldSelectFirst) {
      return;
    }
    const firstSourceOption = options[0];
    if (!firstSourceOption) {
      return;
    }
    const firstSelectOption = optionSourceToSelect(firstSourceOption);
    if (!firstSelectOption.value) {
      return;
    }
    this.updateValue(firstSelectOption.value);
  };

  setRemoteOptions = (optionsResult: LoadOptionsResult<O>): void => {
    const [options, incomplete] = explodeLoadOptionsResult(optionsResult);
    this.setState(
      {
        remoteOptions: options,
        incomplete: incomplete ?? false,
      },
      () => {
        this.props.onRemoteOptionsSet?.();
        this.refreshOptionsListPosition();
      },
    );
    this.tryToSelectFirstOption(options);
  };

  setSelectedOptions = (options: O[]): void => {
    this.setState(
      {
        selectedOptions: options,
      },
      () => {
        this.refreshOptionsListPosition();
      },
    );
  };

  loadCurrentOptions = (): Promise<void> => {
    const { loadSelectedOptions, value, plainMultiValues, isMulti } =
      this.props;
    if (!loadSelectedOptions) {
      return Promise.resolve();
    }
    if (isMulti && !plainMultiValues) {
      return Promise.resolve();
    }
    if (!value) {
      return Promise.resolve();
    }
    // Only single values and plain multi values reach here, and a plain multi
    // value is a list of option ids.
    const ids = this.isSingleValue(value)
      ? [value]
      : value.map((item) => valueItemId(this.props.valueSourceToSelect(item)));
    const renderOptionIds = new Set(
      this.getRenderOptions().map((option) => option.value),
    );
    if (ids.every((id) => renderOptionIds.has(id))) {
      return Promise.resolve();
    }

    if (!value) {
      return Promise.resolve();
    }

    return loadSelectedOptions(ids).then((data) => {
      if (this.componentUnmounted) {
        return;
      }

      this.setSelectedOptions(data);
    });
  };

  getRemoteCacheKey = (query = ''): string =>
    `select_field_${this.id}_${query}`;

  loadRemoteOptions = (query = ''): Promise<void> => {
    const { loadOptions, emptyOptionsOnEmptyFilter } = this.props;

    if (emptyOptionsOnEmptyFilter && !this.state.filterValue) {
      return Promise.resolve();
    }

    if (!loadOptions) {
      return Promise.resolve();
    }

    const cacheKey = this.getRemoteCacheKey(query);
    if (this.remoteOptionsCache.has(cacheKey)) {
      const remoteOptions = this.remoteOptionsCache.get(cacheKey);
      if (remoteOptions) {
        this.setRemoteOptions(remoteOptions);
      }
      this.setState({
        isLoading: false,
      });
      return Promise.resolve();
    }

    if (query !== this.state.filterValue) {
      return Promise.resolve();
    }

    this.setState({
      isLoading: true,
    });

    const promise = loadOptions(query)
      .then((result) => {
        if (this.componentUnmounted) {
          return;
        }

        this.remoteOptionsCache.set(cacheKey, result);
        if (this.promises.length === 1 && query === this.state.filterValue) {
          const remoteOptionsResult = this.remoteOptionsCache.get(cacheKey);
          if (remoteOptionsResult) {
            this.setRemoteOptions(remoteOptionsResult);
          }
          this.setState({
            isLoading: false,
          });
        }
      })
      .finally(() => {
        this.promises.shift();
      })
      .catch(() => {
        if (this.componentUnmounted) {
          return;
        }
        this.setState({
          isLoading: false,
        });
      });
    this.promises.push(promise);

    return promise;
  };

  loadRemoteOptionsDebounce = debounce(
    this.loadRemoteOptions,
    INPUT_DEBOUNCE_TIMEOUT,
  );

  handleFilterValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const filterValue = event.currentTarget.value;
    const { loadOptions, emptyOptionsOnEmptyFilter } = this.props;
    const skipLoad = emptyOptionsOnEmptyFilter && !filterValue;
    if (loadOptions) {
      if (skipLoad) {
        this.setState({
          remoteOptions: [],
        });
      } else {
        const cacheKey = this.getRemoteCacheKey(filterValue);
        if (this.remoteOptionsCache.has(cacheKey)) {
          const remoteOptionsResult = this.remoteOptionsCache.get(cacheKey);
          if (remoteOptionsResult) {
            const [options, incomplete] =
              explodeLoadOptionsResult(remoteOptionsResult);
            this.setState({
              remoteOptions: options,
              incomplete: incomplete ?? false,
            });
          }
          this.setState({
            isLoading: false,
          });
        } else {
          this.setState({
            isLoading: true,
          });
          this.loadRemoteOptionsDebounce(filterValue);
        }
      }
    }

    if (!this.state.show) {
      this.open();
    }

    this.setState(
      {
        hoverIndex: 0,
        filterValue,
        newOptionName: filterValue,
      },
      () => {
        this.refreshOptionsListPosition();
      },
    );
  };

  handleArrowButtonMouseDown = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault();
    if (this.state.show) {
      this.close();
    } else {
      const { current } = this.filterInputRef;
      if (current) {
        current.focus();
        this.open();
      }
    }
  };

  handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    this.close();
    this.setState({ focused: false });
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  };

  handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    this.open();
    this.setState({ focused: true });
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  };

  handleClick = (): void => {
    this.open();
    this.setState({ focused: true });
  };

  handleValueDelete = (valueToDelete: IO): void => {
    const value = this.props.value;
    if (!this.isMultiValue(value)) {
      return;
    }
    const fieldValue = value.map(this.props.valueSourceToSelect);
    const updatedValue = fieldValue.filter(
      (v) =>
        (this.props.plainMultiValues ? v : v.value) !== valueToDelete.value,
    );
    this.updateValue(updatedValue);
  };

  handleValueChange = (valueToChange: IO, index: number): void => {
    const value = this.props.value;
    if (!this.isMultiValue(value)) {
      return;
    }
    const fieldValue = value.map(this.props.valueSourceToSelect);
    this.updateValue(updateObjectAtIndex(fieldValue, index, valueToChange));
  };

  getSelectValue = (
    value: string | V[] | undefined = [],
  ): SelectValueItem<IO>[] | string => {
    if (this.isMultiValue(value)) {
      return value.map(this.props.valueSourceToSelect);
    }
    if (isString(value)) {
      return value;
    }
    return '';
  };

  getValuesAfterDeselectionAll = (
    valueOption: SelectValueItem<IO>[],
    renderOptions: IO[],
  ) => {
    // remove all rendered checked options from input field
    const renderedOptionValues = new Set(
      renderOptions
        .filter((option) => !NEW_OPTION_VALUE_REGEX.test(option.value))
        .map((option) => option.value),
    );

    return valueOption.filter(
      (item) => !renderedOptionValues.has(valueItemId(item)),
    );
  };

  getValuesAfterSelectionAll = (
    valueOption: SelectValueItem<IO>[],
    renderOptions: IO[],
  ) => {
    // Select All: add all rendered options to input field
    return valueOption.concat(
      renderOptions.filter(
        (option) => !NEW_OPTION_VALUE_REGEX.test(option.value),
      ),
    );
  };

  render(): ReactNode {
    const {
      value: valueProp,
      readOnly,
      isCreatorMode,
      isFormDisabled,
      canClear,
      isMulti,
      valueComponent,
      optionComponent,
      placeholder: withOptionsPlaceholder,
      noOptionsPlaceholder,
      creatorText,
      creatorAction,
      name,
      id,
      plainMultiValues,
      className,
      preloadOptions,
      hideOptionsList,
      markedValueElements,
      aria,
    } = this.props;

    const {
      filterValue,
      hoverIndex,
      isLoading,
      incomplete,
      remoteOptionsInitialized,
      copySelection,
    } = this.state;

    const isCopyMode = this.isValueCopyMode();
    // Hide the options popover while marking values to copy.
    const shouldHideOptionsList = hideOptionsList || isCopyMode;

    const valueOption = this.getSelectValue(valueProp);

    const renderOptions = this.getRenderOptions();
    const allOptions = this.getAllOptions();
    const selectedOptionIndex = renderOptions.findIndex(
      (option) =>
        isString(valueOption) &&
        removeSuffix(option.value) === removeSuffix(valueOption),
    );
    const selectedOption =
      selectedOptionIndex !== -1 ? renderOptions[selectedOptionIndex] : null;
    const currentLabel = selectedOption ? selectedOption.label : '';

    const isNewOptionSelected =
      selectedOption && NEW_OPTION_VALUE_REGEX.test(selectedOption.value);
    let isDisabled = isFormDisabled || readOnly;

    const ValueComponent = valueComponent ?? Value;
    const OptionComponent = optionComponent ?? Option;

    let nothingFoundDescription;
    if (!isLoading && !this.props.emptyOptionsOnEmptyFilter) {
      nothingFoundDescription = (
        <div className={styles.nothing_found}>
          {isCreatorMode ? creatorText : 'No options matching'}
        </div>
      );
    }

    if (
      !isLoading &&
      this.props.emptyOptionsOnEmptyFilter &&
      !this.state.filterValue
    ) {
      nothingFoundDescription = (
        <div className={styles.nothing_found}>
          Begin typing to find your desired option
        </div>
      );
    }

    let incompleteDescription;
    if (incomplete && !isLoading) {
      incompleteDescription = (
        <div className={styles.incomplete}>
          <Icon name="help_tooltip" size="xs" />
          <div className={styles.incomplete_message}>
            {filterValue.length === 0
              ? 'Begin typing to find your desired option'
              : 'Continue typing to find your desired option'}
          </div>
        </div>
      );
    }

    const placeholder =
      noOptionsPlaceholder && renderOptions.length === 0
        ? noOptionsPlaceholder
        : withOptionsPlaceholder;

    let placeholderText;
    if (!isMulti) {
      placeholderText = currentLabel || placeholder;
    } else {
      placeholderText = valueOption.length === 0 ? placeholder : '';
    }

    if (preloadOptions && !remoteOptionsInitialized) {
      isDisabled = true;
      placeholderText = 'Loading...';
    }

    const optionsListId = `options_list_${id}`;
    let checkedOptions: string[] = [];
    const optionsListHaveCheckboxes =
      this.props.isMulti && this.props.withCheckboxSelection;
    if (isArray(valueOption) && optionsListHaveCheckboxes) {
      // options in the popover list appear checked if they exist in the input field
      const valueIds = new Set(valueOption.map(valueItemId));
      checkedOptions = renderOptions
        .filter((option) => valueIds.has(option.value))
        .map((option) => option.value);
    }

    const checkedOptionIds = new Set(checkedOptions);
    const hasAllOptionsChecked = renderOptions
      .filter((option) => !NEW_OPTION_VALUE_REGEX.test(option.value))
      .every((option) => checkedOptionIds.has(option.value));

    const optionListHasOnlyNewOption =
      renderOptions.length === 1 &&
      NEW_OPTION_VALUE_REGEX.test(renderOptions[0].value);

    const shouldDisableSelectAllButton = isCreatorMode
      ? !this.state.filterValue || optionListHasOnlyNewOption
      : false;

    return (
      <div
        className={classNames(styles.root, className, {
          [styles.focused]: this.state.focused,
          [styles.disabled]: isDisabled,
        })}
        ref={this.rootRef}
      >
        <div
          className={styles.values_wrapper}
          role={isMulti ? 'list' : undefined}
        >
          {isMulti &&
            Array.isArray(valueOption) &&
            valueOption.map((v, index) => {
              let itemValue =
                typeof v === 'string'
                  ? allOptions.find((option) => option.value === v)
                  : v;
              if (!itemValue) {
                return null;
              }
              itemValue = this.props.processOptionValue(itemValue);
              if (isCopyMode) {
                itemValue = {
                  ...itemValue,
                  isCopyMode: true,
                  isSelectedToCopy: copySelection.includes(itemValue.value),
                };
              }
              const itemCopyValue = itemValue.value;
              return NEW_OPTION_VALUE_REGEX.test(itemValue.value) ? (
                <div
                  key={`option_${itemValue.value}`}
                  className={styles.option_container}
                >
                  {
                    <div
                      role="status"
                      aria-label="New"
                      className={styles.new_multi_option_badge}
                    />
                  }
                  <ValueComponent<IO>
                    value={itemValue}
                    isDisabled={isDisabled || itemValue.disabled}
                    onDelete={this.handleValueDelete}
                    onChange={(changedValue): void =>
                      this.handleValueChange(changedValue, index)
                    }
                    onCopySelectionToggle={() =>
                      this.toggleCopySelection(itemCopyValue)
                    }
                  />
                </div>
              ) : (
                <ValueComponent<IO>
                  key={index}
                  value={itemValue}
                  isDisabled={isDisabled || itemValue.disabled}
                  onDelete={this.handleValueDelete}
                  onChange={(changedValue): void =>
                    this.handleValueChange(changedValue, index)
                  }
                  onCopySelectionToggle={() =>
                    this.toggleCopySelection(itemCopyValue)
                  }
                />
              );
            })}
          <input
            name={`${name}_${getRandomHex(8)}`}
            id={id}
            placeholder={placeholderText}
            type="text"
            data-ui="filter_input"
            className={classNames(styles.filter_input, {
              [styles.filter_input_new]: isNewOptionSelected && !isMulti,
              [styles.filter_multi_input_new]: isNewOptionSelected && isMulti,
            })}
            value={filterValue}
            onChange={this.handleFilterValueChange}
            onFocus={this.handleFocus}
            onClick={this.handleClick}
            onBlur={this.handleBlur}
            ref={this.filterInputRef}
            disabled={isDisabled}
            autoComplete="off"
            data-1p-ignore
            {...ui({ 'input-name': name })}
            {...ariaToReactProps(aria)}
            aria-haspopup="true"
            aria-expanded={this.state.show}
            aria-controls={optionsListId}
          />
        </div>
        {!isMulti && isNewOptionSelected && (
          <div
            role="status"
            aria-label="New"
            className={styles.new_option_badge}
          />
        )}
        <div className={styles.actions}>
          {valueProp &&
          this.isSingleValue(valueProp) &&
          markedValueElements?.[valueProp]
            ? markedValueElements[valueProp]
            : null}
          {this.props.warning?.warningLabel &&
          this.props.warning.flaggedValue === valueProp ? (
            <TooltipTrigger tooltipText={this.props.warning?.warningLabel}>
              <div className={styles.warning_icon}>
                <Icon name="message_warning" size="xs" />
              </div>
            </TooltipTrigger>
          ) : null}
          {valueOption && valueOption.length > 0 && canClear ? (
            <button
              type="button"
              data-ui="reset-button"
              aria-label="Clear"
              tabIndex={-1}
              className={classNames(styles.reset_button, mixins.reset_button)}
              onClick={() =>
                this.updateValue(isMulti ? [] : this.props.clearValue)
              }
              disabled={isDisabled}
            >
              <Icon name="close" />
            </button>
          ) : null}

          <button
            type="button"
            tabIndex={-1}
            data-ui="drop-button"
            className={classNames(styles.drop_button, mixins.reset_button)}
            onMouseDown={this.handleArrowButtonMouseDown}
            disabled={isDisabled}
            aria-label={this.state.show ? 'Close' : 'Open'}
            aria-haspopup="true"
            aria-expanded={this.state.show}
            aria-controls={optionsListId}
          >
            <Icon
              name="chevron_small_isolated_s"
              size="xs"
              className={styles.drop_icon}
            />
          </button>
        </div>
        <Popover
          ref={this.popoverRef}
          opened={this.state.show}
          vDirection={this.state.popoverDirection}
          hDirection="right"
          animation="expand_vertical"
          onMeasuredOpen={this.refreshOptionsListPosition}
        >
          <div
            className={classNames(styles.options_list, {
              [styles.hide_options_list]: shouldHideOptionsList,
              [styles.with_checkbox_selection]:
                optionsListHaveCheckboxes && renderOptions.length > 0,
            })}
            ref={this.optionsListRef}
            data-ui="options-list"
            role="listbox"
            id={optionsListId}
            tabIndex={0}
          >
            {isLoading && <LoadingBar className={styles.loading_bar} />}
            {renderOptions.length ? (
              <>
                {optionsListHaveCheckboxes ? (
                  <div
                    className={styles.options_header}
                    ref={this.optionsListHeaderRef}
                  >
                    {hasAllOptionsChecked && !shouldDisableSelectAllButton ? (
                      <Button
                        onMouseDown={(event) => {
                          event.preventDefault();
                          if (this.isMultiSelectOption(valueOption)) {
                            this.updateValue(
                              this.getValuesAfterDeselectionAll(
                                valueOption,
                                renderOptions,
                              ),
                            );
                          }
                        }}
                        noBorder
                        iconName="close"
                      >
                        Deselect all
                      </Button>
                    ) : (
                      <Button
                        disabled={shouldDisableSelectAllButton}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          if (this.isMultiSelectOption(valueOption)) {
                            this.updateValue(
                              this.getValuesAfterSelectionAll(
                                valueOption,
                                renderOptions,
                              ),
                            );
                          }
                        }}
                        noBorder
                        iconName="checkmark"
                      >
                        Select all
                      </Button>
                    )}
                  </div>
                ) : null}
                {renderOptions.map((option, index) => (
                  <div
                    data-value={option.value}
                    key={index}
                    data-ui="option-item"
                    role="option"
                    aria-selected={index === selectedOptionIndex}
                    tabIndex={-1}
                    className={classNames(styles.option, {
                      [styles.option_hover]: index === hoverIndex,
                      [styles.option_selected]: index === selectedOptionIndex,
                      [styles.options_header_space]:
                        optionsListHaveCheckboxes && index === 0,
                    })}
                    onMouseEnter={() => this.setState({ hoverIndex: index })}
                    onMouseDown={(event) => {
                      if (this.isMultiSelectOption(valueOption)) {
                        const shouldUncheckOption =
                          optionsListHaveCheckboxes &&
                          checkedOptions.includes(option.value);

                        const updatedValueOption = shouldUncheckOption
                          ? valueOption.filter(
                              // remove option from selected options
                              (item) => valueItemId(item) !== option.value,
                            )
                          : valueOption.concat(
                              // add option to selected options
                              plainMultiValues ? option.value : option,
                            );
                        this.updateValue(updatedValueOption);
                      } else {
                        this.updateValue(option.value);
                      }
                      if (
                        !optionsListHaveCheckboxes ||
                        NEW_OPTION_VALUE_REGEX.test(option.value)
                      ) {
                        this.close();
                      }

                      /* prevent stealing focus from input on options clicks */
                      event.preventDefault();
                    }}
                  >
                    <OptionComponent
                      option={option}
                      creatorAction={creatorAction}
                      creatorValue={NEW_OPTION_VALUE}
                      isCreatorValue={NEW_OPTION_VALUE_REGEX.test(option.value)}
                    />
                    {optionsListHaveCheckboxes &&
                    !NEW_OPTION_VALUE_REGEX.test(option.value) ? (
                      <CheckboxBasic
                        className={styles.option_checkbox}
                        value={
                          checkedOptions.includes(option.value)
                            ? 'checked'
                            : null
                        }
                      />
                    ) : (
                      <div className={styles.option_markers}>
                        {markedValueElements?.[option.value]
                          ? markedValueElements[option.value]
                          : null}
                        {index === selectedOptionIndex ? (
                          <Icon name="checkmark" />
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
                {incomplete ? incompleteDescription : null}
              </>
            ) : (
              nothingFoundDescription
            )}
          </div>
        </Popover>
      </div>
    );
  }
}

export default SelectField;
